import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HealthGoalsList from '../components/healthgoals/HealthGoalsList';
import HealthGoalsTestsList from '../components/healthgoals/HealthGoalsTestsList';
import HealthGoalsModal from '../components/healthgoals/HealthGoalsModal';
import HealthGoalsTestsModal from '../components/healthgoals/HealthGoalsTestsModal';
import { healthGoalsService } from '../services/healthGoalsService';
import { toast } from 'react-toastify';
import Spinner from '../components/common/Spinner';

const HealthGoalsPage = () => {
  const [activeTab, setActiveTab] = useState('goals');
  const [loading, setLoading] = useState(true);
  
  // Goals states
  const [goals, setGoals] = useState([]);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalModalMode, setGoalModalMode] = useState('create');
  const [goalSearch, setGoalSearch] = useState('');
  
  // Tests states
  const [tests, setTests] = useState([]);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testModalMode, setTestModalMode] = useState('create');
  const [filters, setFilters] = useState({
    goal_id: '',
    status: ''
  });

  const fetchGoals = async () => {
    try {
      const data = await healthGoalsService.getAllGoals();
      setGoals(data.filter(goal => goal.is_active));
    } catch (error) {
      toast.error('Failed to fetch health goals');
      console.error('Error fetching health goals:', error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await fetch('https://vitaldev.vitalinsights.in/health/health-goal-tests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setTests(data.data || []);
    } catch (error) {
      toast.error('Failed to fetch health goal tests');
      console.error('Error fetching tests:', error);
    }
  };

  const filteredGoals = goals.filter(goal =>
    goal.goal_name.toLowerCase().includes(goalSearch.toLowerCase())
  );

  const filteredTests = tests
    .filter(test => {
      const matchesGoal = !filters.goal_id || test.goal_id === parseInt(filters.goal_id);
      const matchesStatus = !filters.status || (filters.status === 'active' ? test.is_active : !test.is_active);
      return matchesGoal && matchesStatus;
    })
    .sort((a, b) => b.id - a.id); // Sort by ID in descending order

  const resetFilters = () => {
    setFilters({ goal_id: '', status: '' });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Goals CRUD
  const handleCreateGoal = () => {
    setSelectedGoal(null);
    setGoalModalMode('create');
    setShowGoalModal(true);
  };

  const handleEditGoal = (goal) => {
    setSelectedGoal(goal);
    setGoalModalMode('edit');
    setShowGoalModal(true);
  };

  const handleDeleteGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await healthGoalsService.deleteGoal(goalId);
        toast.success('Goal deleted successfully');
        fetchGoals();
      } catch (error) {
        toast.error('Failed to delete goal');
      }
    }
  };

  const handleSaveGoal = async (goalData) => {
    try {
      // Transform biomarkers array to store only IDs
      const transformedData = {
        ...goalData,
        biomarkers: goalData.biomarkers && Array.isArray(goalData.biomarkers)
          ? goalData.biomarkers.map(b => typeof b === 'object' ? b.id : b)
          : []
      };

      if (goalModalMode === 'create') {
        await healthGoalsService.createGoal(transformedData);
        toast.success('Goal created successfully');
      } else {
        await healthGoalsService.updateGoal(selectedGoal.id, transformedData);
        toast.success('Goal updated successfully');
      }
      setShowGoalModal(false);
      fetchGoals();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  // Tests CRUD
  const handleCreateTest = () => {
    setSelectedTest(null);
    setTestModalMode('create');
    setShowTestModal(true);
  };

  const handleEditTest = (test) => {
    setSelectedTest(test);
    setTestModalMode('edit');
    setShowTestModal(true);
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        const response = await fetch(`https://vitaldev.vitalinsights.in/health/health-goal-tests/${testId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ is_active: false })
        });
        if (response.ok) {
          toast.success('Test deleted successfully');
          fetchTests();
        }
      } catch (error) {
        toast.error('Failed to delete test');
      }
    }
  };

  const handleSaveTest = async (testData) => {
    try {
      const method = testModalMode === 'create' ? 'POST' : 'PUT';
      const url = testModalMode === 'create' 
        ? 'https://vitaldev.vitalinsights.in/health/health-goal-tests'
        : `https://vitaldev.vitalinsights.in/health/health-goal-tests/${selectedTest.id}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(testData)
      });

      if (response.ok) {
        toast.success(testModalMode === 'create' ? 'Test created successfully' : 'Test updated successfully');
        setShowTestModal(false);
        fetchTests();
      }
    } catch (error) {
      toast.error('Failed to save test');
    }
  };

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'goals') {
      fetchGoals().then(() => setLoading(false));
    } else {
      Promise.all([fetchTests(), fetchGoals()]).then(() => setLoading(false));
    }
  }, [activeTab]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* <h1 className="text-3xl font-bold text-gray-800 mb-6">Health Goals Management</h1> */}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('goals')}
              className={`py-3 px-1 font-medium transition-colors ${
                activeTab === 'goals'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Goals
            </button>
            <button
              onClick={() => setActiveTab('tests')}
              className={`py-3 px-1 font-medium transition-colors ${
                activeTab === 'tests'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Tests
            </button>
          </div>
        </div>

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div>
            <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Goals</label>
                <input
                  type="text"
                  value={goalSearch}
                  onChange={(e) => setGoalSearch(e.target.value)}
                  placeholder="Search by goal name..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleCreateGoal}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                + Add New Goal
              </button>
            </div>
            <HealthGoalsList
              goals={filteredGoals}
              onEdit={handleEditGoal}
              onDelete={handleDeleteGoal}
            />
          </div>
        )}

        {/* Tests Tab */}
        {activeTab === 'tests' && (
          <div>
            <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Goal</label>
                <select
                  value={filters.goal_id}
                  onChange={(e) => handleFilterChange('goal_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Goals</option>
                  {goals.map((goal) => (
                    <option key={goal.id} value={goal.id}>
                      {goal.goal_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset Filters
                </button>
                <button
                  onClick={handleCreateTest}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  + Add New Test
                </button>
              </div>
            </div>
            <HealthGoalsTestsList
              tests={filteredTests}
              goals={goals}
              onEdit={handleEditTest}
              onDelete={handleDeleteTest}
            />
          </div>
        )}
      </div>

      {/* Goal Modal */}
      {showGoalModal && (
        <HealthGoalsModal
          isOpen={showGoalModal}
          onClose={() => setShowGoalModal(false)}
          onSave={handleSaveGoal}
          goal={selectedGoal}
          mode={goalModalMode}
        />
      )}

      {/* Test Modal */}
      {showTestModal && (
        <HealthGoalsTestsModal
          isOpen={showTestModal}
          onClose={() => setShowTestModal(false)}
          onSave={handleSaveTest}
          test={selectedTest}
          goals={goals}
          mode={testModalMode}
        />
      )}
    </Layout>
  );
};

export default HealthGoalsPage;
